import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkAddPermission, checkDeletePermission,  checkEditPermission,  handleExportFile, handleImportClickFile, handleUpdateAllCheckbox, handleUpdateDataCheckbox } from '../../../Utils/helper';
import Import from '../../../Utils/import';
import { getApi, postApi, putApi, deleteApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { TableFooter } from '../../../Utils/helper';

function Customer() {
  const [methodType, setMethodType] = useState('');
  const { data } = useSelector((state) => state.roleData);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customerSubGroupsList, setCustomerSubGroupsList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pageCount, setPageCount] = useState(1);
  const [editId, setEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState({
    code: '',
    customerGroup: '',
    labelName: '',
    customerSubGroup: ''
  });

  const [formData, setFormData] = useState({
    code: '',
    customerGroup: '',
    labelName: '',
    customerSubGroup: [] // Now will store objects with id and name
  });

  // For dropdown
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [newSubGroup, setNewSubGroup] = useState('');
  const dropdownRef = useRef(null);
  const subGroupRef = useRef(null);

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const handleAddSubGroup = async () => {
    if (newSubGroup.trim()) {
      try {
        setIsLoading(true);
        const response = await postApi('/customer-sub-group', { name: newSubGroup.trim() });

        if (response?.success) {
          toast.success('Sub group added successfully');
          fetchSubCustomerGroups();
          setNewSubGroup('');
        } else {
          toast.error(response?.msg || 'Failed to add sub group');
        }
      } catch (error) {
        toast.error(error.message || 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubGroupChange = (subGroupId, subGroupName) => {
    setFormData(prev => {
      const newSelected = prev.customerSubGroup.some(item => item.id === subGroupId)
        ? prev.customerSubGroup.filter(item => item.id !== subGroupId)
        : [...prev.customerSubGroup, { id: subGroupId, name: subGroupName }];

      return {
        ...prev,
        customerSubGroup: newSelected
      };
    });

    if (formErrors.customerSubGroup) {
      setFormErrors(prev => ({
        ...prev,
        customerSubGroup: ''
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      code: '',
      customerGroup: '',
      labelName: '',
      customerSubGroup: ''
    };

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
      isValid = false;
    }

    if (!formData.customerGroup.trim()) {
      newErrors.customerGroup = 'Customer Group is required';
      isValid = false;
    }

    if (!formData.labelName.trim()) {
      newErrors.labelName = 'Label Name is required';
      isValid = false;
    }

    if (formData.customerSubGroup.length === 0) {
      newErrors.customerSubGroup = 'At least one Customer Sub Group is required';
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const fetchCustomerGroups = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getApi(
        `/customer-group?search=${encodeURIComponent(searchQuery)}&page=${currentPage}&per_page=${itemsPerPage}`
      );

      if (response?.success) {
        setCustomerGroups(response?.data?.customerGroups?.data || []);
        setPageCount(response?.data?.customerGroups?.last_page || 1);
      } else {
        setError(response?.msg || 'Failed to fetch customer groups');
        toast.error(response?.msg || 'Failed to fetch customer groups');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch customer groups');
      toast.error('Failed to fetch customer groups');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubCustomerGroupsDetails = async (id) => {
    try {
      setIsLoading(true);
      setError(null);
      const headers = {
        'Accept': 'application/json'
      };
      
      const response = await getApi(`/customer-group/${id}`,{headers});

      if (response?.success) {
        setCustomerSubGroupsList(response?.data?.customerGroup || []);
      }
    } catch (error) {
      // Error handling remains the same
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(()=>{
    if (customerSubGroupsList?.sub_groups) {
      const allSubGroups = customerSubGroupsList.sub_groups.map(item => ({
        id: item.id,
        name: item.name || item.id // Fallback to id if name doesn't exist
      }));
      setFormData(prev => ({
        ...prev,
        customerSubGroup: allSubGroups,
      }));
    }
  },[customerSubGroupsList])

  const fetchSubCustomerGroups = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getApi('/customer-sub-group?per_page=50');

      if (response?.success) {
        setCustomerSubGroups(response.data.customerSubGroups || []);
      } else {
        setError(response?.msg || 'Failed to fetch sub customer groups');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch sub customer groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1)
  };

  const handlePageClick = (selected) => {
    setCurrentPage(selected);
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      setIsDeleting(true);
      const response = await deleteApi(`/customer-group/${customerToDelete.id}`);

      if (response?.success) {
        toast.success(response.msg || 'Customer group deleted successfully');
        fetchCustomerGroups();
        setShowDeleteModal(false);
      } else {
        toast.error(response?.msg || 'Failed to delete customer group');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while deleting');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      code: '',
      customerGroup: '',
      labelName: '',
      customerSubGroup: []
    });
    setFormErrors({
      code: '',
      customerGroup: '',
      labelName: '',
      customerSubGroup: ''
    });
  };

  const handleEditClick = (customer) => {
    setEditId(customer.group_id);
    let subGroups = [];
    if (customer.sub_group_ids && customer.sub_group_names) {
      const ids = customer.sub_group_ids.split(',');
      const names = customer.sub_group_names.split(',');
      subGroups = ids.map((id, index) => ({
        id: id.trim(),
        name: names[index]?.trim() || `Sub-group ${id}`
      }));
    }

    setFormData({
      code: customer.code,
      customerGroup: customer.name || '',
      labelName: customer.label || '',
      customerSubGroup: subGroups
    });

    setMethodType('edit');
    fetchSubCustomerGroupsDetails(customer?.group_id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      let response;

      const payload = {
        code: formData.code,
        name: formData.customerGroup,
        label: formData.labelName,
        sub_group_ids: formData.customerSubGroup.map(item => item.id)
      };

      if (methodType === 'add') {
        response = await postApi('/customer-group', payload);
      } else {
        const formData = new URLSearchParams();
        Object.keys(payload).forEach(key => {
          if (Array.isArray(payload[key])) {
            payload[key].forEach(item => formData.append(`${key}[]`, item));
          } else {
            formData.append(key, payload[key]);
          }
        });
        response = await putApi(`/customer-group/${editId}`, formData);
      }

      if (response?.success) {
        toast.success(response.msg || 'Operation successful');
        fetchCustomerGroups();
        setMethodType('');
        handleReset();
      } else {
        toast.error(response?.msg || 'Operation failed');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomerGroups();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchSubCustomerGroups();
  }, []);

  return (
    <div className="RightSideDiv cutomer-group product-single category">
      {showImportModal && <Import setImportFile={setImportFile} importFile={importFile} searchQuery={searchQuery} fetchData={fetchCustomerGroups} setShowImportModal={setShowImportModal} routeEnd={'/customer-group/import'} routeexport={'/customer-group/export'} type={'Customer'} />}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                onClick={() => setShowDeleteModal(false)}
                className="close"
                alt="Close"
              />
            </div>
            <div className="modal-body mb-0 p-0">
              <p>Are you sure you want to delete the customer group: <strong>{customerToDelete?.name}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="reset" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="save mt-0"
                onClick={confirmDeleteCustomer}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {methodType === '' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table table-border-show">
              <div className="table-header">
                <div className="search-card">
                  <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Customer Group..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, 'customer_group') && (
                  <button className="btn" onClick={() => handleImportClickFile(setShowImportModal, setImportFile, setImportErrors)}>
                    <img src={window.getPublicUrl('/assets/images/import.svg')} alt="" />
                    Import
                  </button>
                )}
                  <button className="btn" onClick={() => handleExportFile(customerGroups, searchQuery, 'customer.xlsx', '/customer-group/export')}>
                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="" />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, 'customer_group') && (
                    <button className="btn add" onClick={() => setMethodType('add')}>+ Add New customer</button>
                  )}
                </div>
              </div>

              <div className="permission-table-inner category-table">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input type="checkbox" onClick={(e) => handleUpdateAllCheckbox(e?.target?.checked, setCustomerGroups)} />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Code</th>
                      <th className="name">Customer Group</th>
                      <th className="name">Customer Sub Group</th>
                      <th className="name">Label Name</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="6" className="text-center text-danger">{error}</td>
                      </tr>
                    ) : customerGroups.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">No customer groups found</td>
                      </tr>
                    ) : (
                      customerGroups.map((customer) => (
                        <tr key={customer.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input type="checkbox" onClick={(e) => handleUpdateDataCheckbox(customer, e, setCustomerGroups)} checked={customer?.status} />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{customer.code}</td>
                          <td className="name">{customer.name}</td>
                          <td className="name">{customer.sub_group_name || '-'}</td>
                          <td className="origin">{customer.label}</td>
                          <td className="action action-cs">
                            <div className="dropdown">
                                 {(checkEditPermission(data?.permissions, 'customer_group') || checkDeletePermission(data?.permissions, 'customer_group')) && (
                              <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button>
                                 )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li> 
                                   {checkEditPermission(data?.permissions, 'customer_group') && (
                                    <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); handleEditClick(customer); }}>
                                  Edit
                                </a>
                                   )}
                                </li>
                                <li>
                                    {checkDeletePermission(data?.permissions, 'customer_group') && (
                                  <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); handleDeleteClick(customer); }}>
                                    Delete
                                  </a>
                                    )}
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

              </div>
              
              <div className="table-footer d-flex justify-content-between table-pagination">
                  <select
                    name=""
                    id=""
                    className="affter-change-cs page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                  </select>
                  <div>
                    <TableFooter
                      pageCount={pageCount}
                      handlePageClick={handlePageClick}
                      currentPage={currentPage}
                    />
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {(methodType === 'add' || methodType === 'edit') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>{methodType === 'add' ? 'Add New' : 'Edit'} Customer Group</h3>
              <div className="button-settings">
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-end gap-3">
                        <button type="button" className="reset" onClick={handleReset}>
                          Reset
                        </button>
                        <button type="submit" className="save" onClick={handleSubmit} disabled={isLoading}>
                          {isLoading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div> 
              <img
                src={window.getPublicUrl("/assets/images/close.svg")}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  handleReset();
                }}
                
              />
              <div className="setting-inner pb-0">
                <form >
                  <div className='curtomer-group-scroll'>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>
                            Code<strong>*</strong>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.code ? 'is-invalid' : ''}`}
                            placeholder="Enter Code"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                          />
                          {formErrors.code && <div className="invalid-feedback">{formErrors.code}</div>}
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>
                            Customer Group<strong>*</strong>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.customerGroup ? 'is-invalid' : ''}`}
                            placeholder="Enter customer group (Traditional Chinese)"
                            name="customerGroup"
                            value={formData.customerGroup}
                            onChange={handleInputChange}
                          />
                          {formErrors.customerGroup && <div className="invalid-feedback">{formErrors.customerGroup}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>
                            Label Name<strong>*</strong>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.labelName ? 'is-invalid' : ''}`}
                            placeholder="Enter Label Name"
                            name="labelName"
                            value={formData.labelName}
                            onChange={handleInputChange}
                          />
                          {formErrors.labelName && <div className="invalid-feedback">{formErrors.labelName}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="form-group select-drop-down">
                          <label htmlFor="customerSubGroup" className='d-block'>
                            Customer Sub Group <strong>*</strong>
                          </label>
                          <div
                            className={`dropdown oper-drop-down-cs w-100 text-left ${formData.customerSubGroup.length > 0 ? 'change-onselect' : ''} ${formErrors.customerSubGroup ? 'is-invalid' : ''}`}
                            ref={subGroupRef}
                          >
                            <button
                              type="button"
                              onClick={toggleDropdown}
                              className={`dropdown-toggle text-start ${isDropdownVisible ? 'show' : ''}`}
                            >
                              {formData.customerSubGroup.length > 0
                                ? formData.customerSubGroup.map(item => item.name).join(', ')
                                : 'Please Select'}
                              <img src={window.getPublicUrl('/assets/images/choosearrow.png')} alt="" />
                            </button>
                            {isDropdownVisible && (
                              <div className="dropdown-menu show" ref={dropdownRef}>
                                <div className="dropdown-content">
                                  <div className="offering-dropdown-list">
                                    {customerSubGroups.map(subGroup => (
                                      <div key={subGroup.id} className="dropdown-item checkbox-item justify-content-start">
                                              <label className="checbox-cs me-1 mt-2">
                                          <input
                                            type="checkbox"
                                            checked={formData.customerSubGroup.some(item => item.id === subGroup.id)}
                                            onChange={() => handleSubGroupChange(subGroup.id, subGroup.name)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <span className="checkmark"></span>
                                        </label>
                                        <span>{subGroup.name}</span>
                                        {/* <label className="checbox-cs me-3">
                                          <input
                                            type="checkbox"
                                            checked={formData.customerSubGroup.some(item => item.id === subGroup.id)}
                                            onChange={() => handleSubGroupChange(subGroup.id, subGroup.name)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <span className="checkmark"></span>
                                        </label> */}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="offer-search" onClick={(e) => e.stopPropagation()}>
                                    <div className="input-group">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter new sub group"
                                        value={newSubGroup}
                                        onChange={(e) => setNewSubGroup(e.target.value)}
                                      />
                                      <div className="input-group-append">
                                        <button
                                          type="button"
                                          className="btn btn-primary mt-0 px-4"
                                          onClick={handleAddSubGroup}
                                        >
                                          Add
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {formErrors.customerSubGroup && (
                            <div className="invalid-feedback d-block">{formErrors.customerSubGroup}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="button-settings">
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-end gap-3">
                        <button type="button" className="reset" onClick={handleReset}>
                          Reset
                        </button>
                        <button type="submit" className="save" disabled={isLoading}>
                          {isLoading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div> */}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customer;